// this page will handle the club member login
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  TextField,
  InputAdornment,
  IconButton,
  Link,
  Fade,
  LinearProgress,
  Stack,
  Alert,
  Chip,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowBack as BackIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from "@react-oauth/google";
import handleGoogleSuccess from '../../middleware/handleGoogelSuccess';

// Import your components
import api from '../../api'
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import AuthStepper from '../../components/AuthStepper';
import '../../styles/ClubMemberLogin.css';

const ClubMemberLogin = () => {
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isDev = process.env.NODE_ENV !== 'production';
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username_or_email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mounted, setMounted] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // default to max width; will be adjusted on mount/resize to avoid clipping
  const [googleWidth, setGoogleWidth] = useState(380);
  const googleWrapperRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateWidth = () => {
      const containerWidth = googleWrapperRef.current?.clientWidth || window.innerWidth;
      const available = Math.max(200, Math.min(containerWidth - 16, 380));
      setGoogleWidth(available);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleInputChange = (field) => (event) => {
    setFormData({ 
      ...formData, 
      [field]: event.target.value 
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    if (loginError) setLoginError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username_or_email || formData.username_or_email.trim() === '') {
      newErrors.username_or_email = 'Email or username is required';
    }
    
    if (!formData.password || formData.password.trim() === '') {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setLoginError('');
    
    try {
      const response = await api.post('users/login/', {
        username_or_email: formData.username_or_email.trim(),
        password: formData.password.trim(),
        role: 'club_member'
      });

      // if (response?.access) {
      //   try {
      //     localStorage.setItem('access_token', response.access);
      //     if (response.refresh) localStorage.setItem('refresh_token', response.refresh);
      //   } catch {}
      // }

      navigate('/clubmember/welcome', { replace: true });
    } catch (error) {
      const backendDetail = error?.response?.data?.detail;
      const backendMessage = error?.response?.data?.message;
      const backendNonField = Array.isArray(error?.response?.data?.non_field_errors)
        ? error.response.data.non_field_errors[0]
        : null;

      const errorMessage = backendDetail || backendMessage || backendNonField ||
        error?.message ||
        'Login failed. Please check your credentials and try again.';
      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!mounted) return null;

  return (
    <Box 
      className="club-member-login-background"
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #d1fae5 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '-10%',
          width: { xs: '300px', md: '400px' },
          height: { xs: '300px', md: '400px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(1, 111, 87, 0.08) 0%, rgba(1, 154, 120, 0.03) 70%, transparent 100%)',
          animation: 'float 6s ease-in-out infinite',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '-5%',
          width: { xs: '250px', md: '350px' },
          height: { xs: '250px', md: '350px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(1, 154, 120, 0.06) 0%, rgba(1, 111, 87, 0.02) 70%, transparent 100%)',
          animation: 'float 8s ease-in-out infinite reverse',
          zIndex: 0
        }}
      />
      
      {/* Navbar */}
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <Navbar />
      </Box>
      
      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        pt: 4,
        pb: 8,
        position: 'relative',
        zIndex: 1
      }}>
        <Fade in={mounted} timeout={800}>
          <AuthStepper
            currentStep={2}
            totalSteps={2}
            palette={{
              barGradient: 'linear-gradient(90deg, #016f57 0%, #019a78 100%)',
              barShadow: '0 0 10px rgba(1, 111, 87, 0.3)',
              trackColor: 'rgba(148, 163, 184, 0.15)',
              dotGradient: 'linear-gradient(135deg, #016f57, #019a78)',
              dotShadow: '0 2px 8px rgba(1, 111, 87, 0.4)',
            }}
            containerProps={{ maxWidth: 'md', sx: { mb: 4 } }}
            paperProps={{
              elevation: 0,
              sx: {
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
              },
            }}
            dotProps={{
              sx: {
                position: 'absolute',
                top: -2,
                right: -1,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #016f57, #019a78)',
                boxShadow: '0 2px 8px rgba(1, 111, 87, 0.4)',
                animation: 'pulse 2s infinite',
              },
            }}
            leftSlot={(
              <Link
                component="button"
                onClick={() => window.history.back()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#016f57',
                    transform: 'translateX(-2px)',
                  },
                }}
              >
                <BackIcon sx={{ fontSize: 18 }} />
                Back to Role Selection
              </Link>
            )}
            rightSlot={(
              <>
                <Chip
                  icon={<CheckIcon />}
                  label="Step 2 of 2"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #016f57, #019a78)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: 'white',
                    },
                  }}
                />
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Club Member Login
                </Typography>
              </>
            )}
          />
        </Fade>

        <Container maxWidth="sm">
          <Fade in={mounted} timeout={1200}>
            <Card
              className="club-member-login-card"
              sx={{
                borderRadius: 5,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(30px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                overflow: 'visible',
                position: 'relative',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 32px 64px -12px rgba(1, 111, 87, 0.12), 0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              {/* Loading Progress Bar */}
              {loading && (
                <LinearProgress 
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    borderRadius: '20px 20px 0 0',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #016f57 0%, #019a78 100%)',
                      animation: 'shimmer 1s ease-in-out infinite alternate'
                    }
                  }}
                />
              )}

              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: { xs: 72, md: 88 },
                        height: { xs: 72, md: 88 },
                        background: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        boxShadow: '0 8px 32px rgba(1, 111, 87, 0.3)',
                        animation: 'avatarFloat 3s ease-in-out infinite'
                      }}
                    >
                      🤝
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
                        animation: 'pulse 2s infinite'
                      }}
                    >
                      ⭐
                    </Box>
                  </Box>
                  
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #1e293b 0%, #016f57 80%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      letterSpacing: '-0.02em',
                      animation: 'textShine 3s ease-in-out infinite'
                    }}
                  >
                    Lead & Organize!
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#64748b',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontWeight: 500,
                      maxWidth: 400,
                      mx: 'auto',
                      lineHeight: 1.6,
                      mb: 2
                    }}
                  >
                    Access your club management dashboard and coordinate activities, manage teams, and build stronger communities.
                  </Typography>
                </Box>

                {/* Error Alert */}
                {loginError && (
                  <Fade in={!!loginError}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3,
                        borderRadius: 3,
                        backgroundColor: 'rgba(239, 68, 68, 0.05)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        '& .MuiAlert-icon': {
                          fontSize: 20
                        },
                        '& .MuiAlert-message': {
                          fontWeight: 500
                        }
                      }}
                    >
                      {loginError}
                    </Alert>
                  </Fade>
                )}

                {/* Login Form */}
                <Box component="form" onSubmit={handleSubmit} noValidate className="club-member-login-form">
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Email or Username"
                      type="text"
                      name="username_or_email"
                      value={formData.username_or_email}
                      onChange={handleInputChange('username_or_email')}
                      error={!!errors.username_or_email}
                      helperText={errors.username_or_email}
                      placeholder="your.email@college.edu or username"
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#016f57', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          backgroundColor: 'rgba(240, 253, 244, 0.8)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: 'rgba(240, 253, 244, 1)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(1, 111, 87, 0.15)'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 20px rgba(1, 111, 87, 0.2)'
                          },
                          // slightly shorter inputs on mobile
                          height: { xs: 40, md: 'auto' },
                          display: 'flex',
                          alignItems: 'center'
                        },
                        '& .MuiInputBase-input': {
                          padding: { xs: '8px 12px', md: '12px 14px' },
                          height: '100%',
                          boxSizing: 'border-box'
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      error={!!errors.password}
                      helperText={errors.password}
                      placeholder="Enter your password"
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#016f57', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                              sx={{
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(16, 185, 129, 0.1)'
                                }
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          backgroundColor: 'rgba(240, 253, 244, 0.8)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: 'rgba(240, 253, 244, 1)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(1, 111, 87, 0.15)'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 20px rgba(1, 111, 87, 0.2)'
                          },
                          height: { xs: 40, md: 'auto' },
                          display: 'flex',
                          alignItems: 'center'
                        },
                        '& .MuiInputBase-input': {
                          padding: { xs: '8px 12px', md: '12px 14px' },
                          height: '100%',
                          boxSizing: 'border-box'
                        }
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      endIcon={loading ? null : <ArrowIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
                        color: 'white',
                        // slightly larger than inputs on mobile; reduced on desktop
                        py: { xs: '12px', md: '18px' },
                        px: { xs: '18px', md: 4 },
                        borderRadius: { xs: 3, md: 4 },
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: { xs: '0.98rem', md: '1.05rem' },
                        boxShadow: '0 8px 25px rgba(1, 111, 87, 0.4)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: { xs: 52, md: 44 },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 30px rgba(1, 111, 87, 0.5)',
                        },
                        '&:disabled': {
                          background: 'rgba(1, 111, 87, 0.6)',
                          transform: 'none',
                          boxShadow: '0 4px 12px rgba(1, 111, 87, 0.2)',
                          animation: loading ? 'buttonPulse 1.5s ease-in-out infinite' : 'none'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover::before': {
                          left: '100%'
                        }
                      }}
                    >
                      {loading ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              borderTopColor: 'white',
                              animation: 'spin 1s linear infinite'
                            }}
                          />
                          <span>Signing you in...</span>
                        </Stack>
                      ) : (
                        'Access Club Dashboard'
                      )}
                    </Button>
                  </Stack>
                </Box>

                {/* Forgot Password */}
                <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
                  <Link
                    href="/forgot-password?role=club_member"
                    sx={{
                      color: '#016f57',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#019a78'
                      }
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Box>

                {/* Divider */}
                <Divider sx={{ my: 4 }}>
                  <Typography variant="body2" sx={{ 
                    color: '#64748b', 
                    px: 3,
                    fontWeight: 500,
                    backgroundColor: 'white',
                    borderRadius: 2
                  }}>
                    Or continue with
                  </Typography>
                </Divider>

                {/* Social Login */}
                <Stack direction="column" spacing={2} sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      overflowX: 'hidden',
                      p: 1,
                      borderRadius: 4,
                      border: '1px solid #e2e8f0',
                      backgroundColor: 'rgba(240, 253, 244, 0.65)',
                      boxShadow: '0 2px 10px rgba(15, 23, 42, 0.04)',
                      transition: 'all 0.25s ease',
                      '& iframe': {
                        maxWidth: '100% !important',
                      },
                      '& > div': {
                        maxWidth: '100% !important',
                      },
                      '&:hover': {
                        borderColor: '#016f57',
                        backgroundColor: 'rgba(1, 111, 87, 0.06)',
                        boxShadow: '0 10px 25px rgba(1, 111, 87, 0.18)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                    ref={googleWrapperRef}
                  >
                    {googleClientId ? (
                      <GoogleLogin
                        onSuccess={(credentialResponse) =>
                          handleGoogleSuccess(
                            credentialResponse,
                            navigate,
                            setLoading,
                            setLoginError,
                            'club_member',
                            '/clubmember/welcome'
                          )
                        }
                        onError={() => {
                          if (isDev) {
                            console.warn(
                              'Google Sign-In failed. If you are using an in-app browser/webview, open this page in Chrome/Edge and try again.'
                            );
                          }
                          setLoginError('Google Sign-In failed. Please try again.');
                        }}
                        theme="outline"
                        size="large"
                        text="signin_with"
                        shape="pill"
                        logo_alignment="left"
                        width={googleWidth}
                      />
                    ) : (
                      <Alert severity="info" sx={{ width: '100%' }}>
                        Google login is currently unavailable.
                      </Alert>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default ClubMemberLogin;